import React, { useState, memo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatRating } from '../../utils/formatters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faPlay, faCalendarAlt, faStar, faClock, faEye, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const Card = styled(Link)`
  width: 180px;
  height: 320px;
  margin-right: 15px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  flex-shrink: 0;
  
  &:hover {
    transform: translateY(-5px) scale(1.03);
    z-index: 10; /* Keep this below navigation buttons (z-index: 20) */
  }
  
  @media (max-width: 768px) {
    width: 150px;
    height: 260px;
    margin-right: 8px;
  }
`;

const PosterContainer = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  width: 100%;
  height: 270px;
  background-color: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  ${Card}:hover & {
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 768px) {
    height: 210px;
    border-radius: 10px;
  }
`;

const PosterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
  
  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 30px;
  background-color: rgba(26, 26, 26, 0.7);
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color), var(--primary-light));
  width: ${props => props.progress || 0}%;
  z-index: 2;
  border-radius: 0 4px 0 0;
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
`;

const ProgressInfo = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  z-index: 3;
`;

const CardInfo = styled.div`
  padding: 8px 3px;
  height: 50px;
  
  @media (max-width: 768px) {
    height: 40px;
    padding: 6px 2px;
  }
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #fff;
  transition: color 0.2s ease;
  
  ${Card}:hover & {
    color: var(--primary-light);
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 2px;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  
  .year {
    margin-right: 10px;
  }
  
  .rating {
    display: flex;
    align-items: center;
    
    .star {
      color: #ffc107;
      margin-right: 3px;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
    
    .year {
      margin-right: 6px;
    }
  }
`;

const LastWatched = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContentType = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: ${props => props.$customColor || 'rgba(0, 0, 0, 0.6)'};
  backdrop-filter: blur(5px);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  border: 1px solid ${props => props.$customBorderColor || 'rgba(255, 255, 255, 0.1)'};
  transition: all 0.2s ease;
  
  ${Card}:hover & {
    background-color: ${props => props.$hoverColor || 'rgba(59, 130, 246, 0.3)'};
    border-color: ${props => props.$hoverBorderColor || 'rgba(59, 130, 246, 0.5)'};
  }
  
  @media (max-width: 768px) {
    font-size: 8px;
    padding: 2px 6px;
    top: 6px;
    right: 6px;
  }
`;

const ContentIdentifier = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background-color: ${props => props.$bgColor || 'rgba(255, 0, 0, 0.6)'};
  backdrop-filter: blur(5px);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  border: 1px solid ${props => props.$borderColor || 'rgba(255, 0, 0, 0.3)'};
  transition: all 0.2s ease;
  color: ${props => props.$textColor || 'white'};
  display: flex;
  align-items: center;
  z-index: 3;
  
  svg {
    margin-right: 3px;
    font-size: 8px;
  }
  
  ${Card}:hover & {
    background-color: ${props => props.$hoverBgColor || 'rgba(255, 0, 0, 0.7)'};
    border-color: ${props => props.$hoverBorderColor || 'rgba(255, 0, 0, 0.5)'};
  }
  
  @media (max-width: 768px) {
    font-size: 7px;
    padding: 2px 5px;
    bottom: 6px;
    left: 6px;
    
    svg {
      font-size: 7px;
    }
  }
`;

const ViewCount = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    font-size: 9px;
    color: var(--primary-light);
  }
  
  ${Card}:hover & {
    background-color: rgba(59, 130, 246, 0.3);
    border-color: rgba(59, 130, 246, 0.5);
  }
  
  @media (max-width: 768px) {
    font-size: 8px;
    padding: 2px 6px;
    top: 6px;
    left: 6px;
  }
`;

// Updated Preview Modal Components
const PreviewModal = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: rgba(20, 20, 20, 0.95);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 15;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  transform: translateY(0);
  transition: all 0.3s ease;
  pointer-events: none;

  ${Card}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    pointer-events: auto;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const PreviewContent = styled.div`
  padding: 12px;
`;

const PreviewTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
  color: white;
  line-height: 1.3;
`;

const PreviewDetails = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  
  svg {
    color: var(--primary-light);
    margin-right: 5px;
    width: 10px;
  }
`;

const Description = styled.p`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 8px;
`;

const GenreTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const GenreTag = styled.span`
  padding: 1px 6px;
  background-color: rgba(59, 130, 246, 0.15);
  border-radius: 3px;
  font-size: 9px;
  color: var(--primary-light);
  border: 1px solid rgba(59, 130, 246, 0.2);
`;

// Helper function to format date
const formatWatchedDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Parse the UTC date string and ensure proper timezone handling
    const date = new Date(dateString);
    const now = new Date();
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Get date components for comparison
    const dateDay = date.getDate();
    const dateMonth = date.getMonth();
    const dateYear = date.getFullYear();
    
    const nowDay = now.getDate();
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    
    // Compare only the date part
    const isSameDay = dateDay === nowDay && dateMonth === nowMonth && dateYear === nowYear;
    
    // Check for yesterday: subtract one day from now and compare
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = 
      dateDay === yesterday.getDate() && 
      dateMonth === yesterday.getMonth() && 
      dateYear === yesterday.getFullYear();
    
    if (isSameDay) {
      return 'Today';
    } else if (isYesterday) {
      return 'Yesterday';
    } else {
      // Format as MM/DD/YYYY using local timezone
      return date.toLocaleDateString();
    }
  } catch (e) {
    console.error('Error formatting date:', e);
    return '';
  }
};

// Helper function to format view counts
const formatViewCount = (count) => {
  if (!count && count !== 0) return null;
  
  if (count < 1000) return count.toString();
  if (count < 10000) return (count / 1000).toFixed(1) + 'k';
  if (count < 1000000) return Math.floor(count / 1000) + 'k';
  return (count / 1000000).toFixed(1) + 'M';
};

// Helper function to extract content identifiers from title
const extractIdentifier = (title) => {
  if (!title) return { cleanTitle: '', identifier: null };
  
  // Define regex patterns for the identifiers
  const patterns = [
    { regex: /\(UNCENSORED\)/i, id: 'UNCENSORED', 
      style: { 
        bgColor: 'rgba(255, 0, 0, 0.6)', 
        borderColor: 'rgba(255, 0, 0, 0.3)',
        hoverBgColor: 'rgba(255, 0, 0, 0.7)',
        hoverBorderColor: 'rgba(255, 0, 0, 0.5)',
        customColor: 'rgba(0, 0, 0, 0.65)',
        customBorderColor: 'rgba(255, 70, 70, 0.3)',
        hoverColor: 'rgba(0, 0, 0, 0.7)', 
        hoverBorderColor: 'rgba(255, 70, 70, 0.4)'
      }
    },
    { regex: /\(HUNGAMA\)/i, id: 'HUNGAMA', 
      style: { 
        bgColor: 'rgba(255, 165, 0, 0.6)', 
        borderColor: 'rgba(255, 165, 0, 0.3)',
        hoverBgColor: 'rgba(255, 165, 0, 0.7)',
        hoverBorderColor: 'rgba(255, 165, 0, 0.5)',
        textColor: 'white',
        customColor: 'rgba(0, 0, 0, 0.65)',
        customBorderColor: 'rgba(255, 165, 0, 0.3)',
        hoverColor: 'rgba(0, 0, 0, 0.7)',
        hoverBorderColor: 'rgba(255, 165, 0, 0.4)'
      }
    },
    { regex: /\(CN\)/i, id: 'CN', 
      style: { 
        bgColor: 'rgba(0, 150, 255, 0.6)', 
        borderColor: 'rgba(0, 150, 255, 0.3)',
        hoverBgColor: 'rgba(0, 150, 255, 0.7)',
        hoverBorderColor: 'rgba(0, 150, 255, 0.5)',
        customColor: 'rgba(0, 0, 0, 0.65)',
        customBorderColor: 'rgba(0, 150, 255, 0.3)',
        hoverColor: 'rgba(0, 0, 0, 0.7)',
        hoverBorderColor: 'rgba(0, 150, 255, 0.4)'
      }
    }
  ];
  
  let cleanTitle = title;
  let foundIdentifier = null;
  
  // Check for each pattern
  for (const pattern of patterns) {
    if (pattern.regex.test(title)) {
      // Remove the identifier from the title
      cleanTitle = title.replace(pattern.regex, '').trim();
      foundIdentifier = {
        id: pattern.id,
        style: pattern.style
      };
      break; // Stop after finding the first match
    }
  }
  
  return { cleanTitle, identifier: foundIdentifier };
};

// Create a separate component for the card content to avoid conditional hook call
const CardContent = ({ item, type, progress = 0 }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const cardRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Extract content identifier from title
  const { cleanTitle, identifier } = extractIdentifier(item.title);
  
  // -type prop,- fallback to item.type 
  const contentType = type || item.type || (item.seasons ? 'show' : 'movie');
  
  // For continue watching items
  // Handle different ID formats item._id item.id
  const itemId = item._id || item.id;
  let linkPath;
  
  // Check if this is a continue watching item (has progress > 0)
  const isContinueWatching = progress > 0 || item.progress > 0 || item.watchedAt;
  
  if (isContinueWatching) {
    // For continue watching items, go directly to player with URL parameters
    if (contentType === 'movie') {
      linkPath = `/player?movieId=${itemId}`;
      console.log('Continue watching movie:', itemId);
    } else if (contentType === 'episode') {
      const showId = typeof item.showId === 'object' ? item.showId?._id : item.showId;
      linkPath = `/player?episodeId=${itemId}`;
      console.log('Continue watching episode:', itemId, 'from show:', showId);
    } else if (contentType === 'show') {
      linkPath = `/player?showId=${itemId}`;
      console.log('Continue watching show:', itemId);
    } else {
      // Fallback to details page if type is unknown
      linkPath = `/movie/${itemId}`;
    }
  } else if (contentType === 'episode') {
    if (item.showId) {
      linkPath = `/show/${item.showId}`;
    } else if (typeof item.showId === 'object' && item.showId && item.showId._id) {
      linkPath = `/show/${item.showId._id}`;
    } else {
      linkPath = '/shows';
      console.error('Could not determine show ID for episode:', item);
    }
  } else if (contentType === 'movie') {
    // Always navigate to movie details page, not directly to player
    linkPath = `/movie/${itemId}`;
  } else {
    // For shows - use singular form to match App.js routes
    linkPath = `/show/${itemId}`;
  }
  
  const year = item.releaseYear || item.year || item.startYear || '';
  const rating = formatRating(item.rating);
  
  const handleImageLoad = () => {
    setImageLoaded(true);
  };
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  // Handle card click - to ensure navigation buttons don't trigger card actions
  const handleCardClick = (e) => {
    // Check if the click originates from a navigation button
    if (e.target.closest('button[aria-label="Previous content"], button[aria-label="Next content"]')) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  };
  
  // Get runtime in hours and minutes
  const formatRuntime = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };
  
  // Format genres for display
  const genres = item.genres || [];
  
  // Format progress percentage
  const progressPercent = Math.round(progress);
  
  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Only prepare player state for non-continue watching items
  const playerState = !isContinueWatching ? {
    state: {
      id: itemId,
      type: contentType,
      title: item.title || '',
      description: item.description || '',
    }
  } : {};

  // Custom styling for content type based on identifier
  let contentTypeStyle = {};
  if (identifier) {
    if (identifier.id === 'UNCENSORED') {
      contentTypeStyle = {
        $customColor: 'rgba(255, 0, 0, 0.6)',
        $customBorderColor: 'rgba(255, 0, 0, 0.3)',
        $hoverColor: 'rgba(255, 0, 0, 0.7)',
        $hoverBorderColor: 'rgba(255, 0, 0, 0.5)'
      };
    } else if (identifier.id === 'HUNGAMA') {
      contentTypeStyle = {
        $customColor: 'rgba(255, 165, 0, 0.6)',
        $customBorderColor: 'rgba(255, 165, 0, 0.3)',
        $hoverColor: 'rgba(255, 165, 0, 0.7)',
        $hoverBorderColor: 'rgba(255, 165, 0, 0.5)'
      };
    } else if (identifier.id === 'CN') {
      contentTypeStyle = {
        $customColor: 'rgba(0, 150, 255, 0.6)',
        $customBorderColor: 'rgba(0, 150, 255, 0.3)',
        $hoverColor: 'rgba(0, 150, 255, 0.7)',
        $hoverBorderColor: 'rgba(0, 150, 255, 0.5)'
      };
    }
  }
  
  return (
    <Card 
      to={linkPath}
      ref={cardRef}
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
      onClick={handleCardClick}
      state={isContinueWatching ? null : playerState.state}
    >
      <PosterContainer>
        {!imageLoaded && !imageError && (
          <ImagePlaceholder>
            <FontAwesomeIcon icon={faSpinner} spin />
          </ImagePlaceholder>
        )}
        {!imageError && item.image ? (
          <PosterImage 
            src={item.image} 
            alt={item.title} 
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ opacity: imageLoaded ? 1 : 0 }}
          />
        ) : (
          <ImagePlaceholder>
            <FontAwesomeIcon icon={faImage} />
          </ImagePlaceholder>
        )}
        
        {progress > 0 && <ProgressBar progress={progress} />}
        {progress > 0 && <ProgressInfo>{progressPercent}%</ProgressInfo>}
        
        <ContentType {...contentTypeStyle}>{contentType}</ContentType>
        
        {identifier && (
          <ContentIdentifier 
            $bgColor={identifier.style.bgColor}
            $borderColor={identifier.style.borderColor}
            $hoverBgColor={identifier.style.hoverBgColor}
            $hoverBorderColor={identifier.style.hoverBorderColor}
            $textColor={identifier.style.textColor}
          >
            {identifier.id === 'UNCENSORED' && <FontAwesomeIcon icon={faExclamationTriangle} />}
            {identifier.id}
          </ContentIdentifier>
        )}
        
        {(item.views !== undefined && item.views > 0) && (
          <ViewCount>
            <FontAwesomeIcon icon={faEye} />
            {formatViewCount(item.views)}
          </ViewCount>
        )}
      </PosterContainer>
      <CardInfo>
        <Title>{cleanTitle}</Title>
        <MetaInfo>
          {year && <span className="year">{year}</span>}
          {rating !== 'N/A' && (
            <span className="rating">
              <span className="star">★</span>
              {rating}
            </span>
          )}
        </MetaInfo>
        {item.watchedAt && (
          <LastWatched>Last watched: {formatWatchedDate(item.watchedAt)}</LastWatched>
        )}
      </CardInfo>
      
      {/* Simplified Hover Preview Modal - Hidden on mobile */}
      <PreviewModal>
        <PreviewContent>
          <PreviewTitle>{cleanTitle} {identifier && `(${identifier.id})`}</PreviewTitle>
          <PreviewDetails>
            {year && (
              <DetailItem>
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>{year}</span>
              </DetailItem>
            )}
            {rating !== 'N/A' && (
              <DetailItem>
                <FontAwesomeIcon icon={faStar} />
                <span>Rating: {rating}</span>
              </DetailItem>
            )}
            {(item.durationMinutes || item.runtime) && (
              <DetailItem>
                <FontAwesomeIcon icon={faClock} />
                <span>{formatRuntime(item.durationMinutes || item.runtime)}</span>
              </DetailItem>
            )}
          </PreviewDetails>
          {item.description && (
            <Description>{item.description}</Description>
          )}
          {genres.length > 0 && (
            <GenreTags>
              {genres.slice(0, 3).map((genre, index) => (
                <GenreTag key={index}>{genre}</GenreTag>
              ))}
            </GenreTags>
          )}
        </PreviewContent>
      </PreviewModal>
    </Card>
  );
};

// Main component that handles the conditional rendering
const ContentCard = (props) => {
  if (!props.item) return null;
  return <CardContent {...props} />;
};

export default memo(ContentCard);